import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import Label from "./label";
export var useControlledText = function useControlledText(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$initialValue = _ref.initialValue,
      initialValue = _ref$initialValue === void 0 ? "" : _ref$initialValue,
      validator = _ref.validator,
      _ref$errorDelay = _ref.errorDelay,
      errorDelay = _ref$errorDelay === void 0 ? 1000 : _ref$errorDelay;

  var validate = useCallback(function (value) {
    return validator ? validator(value) : [true];
  }, [validator]);

  var _useState = useState(initialValue),
      value = _useState[0],
      setValue = _useState[1];

  var _useState2 = useState(validate(value)[0]),
      isValid = _useState2[0],
      setIsValid = _useState2[1];

  var _useState3 = useState(false),
      isErrorDelayOver = _useState3[0],
      setIsErrorDelayOver = _useState3[1];

  var errorDelayTimeout = useRef();

  var _useState4 = useState(),
      errorMessage = _useState4[0],
      setErrorMessage = _useState4[1];

  var onChange = function onChange(e) {
    setValue(e.target.value);
    setIsErrorDelayOver(false);
    errorDelayTimeout.current && clearTimeout(errorDelayTimeout.current);
    errorDelayTimeout.current = setTimeout(function () {
      setIsErrorDelayOver(true);
    }, errorDelay);
  };

  var onBlur = function onBlur() {
    errorDelayTimeout.current && clearTimeout(errorDelayTimeout.current);
    setIsErrorDelayOver(true);
  };

  useEffect(function () {
    var _validate = validate(value),
        isNewInputValid = _validate[0],
        newErrorMessage = _validate[1];

    setIsValid(isNewInputValid);
    setErrorMessage(newErrorMessage);

    if (isNewInputValid) {
      errorDelayTimeout.current && clearTimeout(errorDelayTimeout.current);
      setIsErrorDelayOver(false);
    }
  }, [value, validate]);
  return {
    value: value,
    isValid: isValid,
    isErrorDelayOver: isErrorDelayOver,
    errorMessage: errorMessage,
    onChange: onChange,
    onBlur: onBlur
  };
};
export var TextInputGroup = function TextInputGroup(_ref2) {
  var label = _ref2.label,
      renderIcon = _ref2.renderIcon,
      name = _ref2.name,
      value = _ref2.value,
      required = _ref2.required,
      disabled = _ref2.disabled,
      isValid = _ref2.isValid,
      errorMessage = _ref2.errorMessage,
      isErrorDelayOver = _ref2.isErrorDelayOver,
      className = _ref2.className,
      style = _ref2.style,
      as = _ref2.as,
      rest = _objectWithoutPropertiesLoose(_ref2, ["label", "renderIcon", "name", "value", "required", "disabled", "isValid", "errorMessage", "isErrorDelayOver", "className", "style", "as"]);

  var iconSize = "1.5em";
  var id = name.replace(/[\W]/g, "");
  var shouldShowError = !isValid && isErrorDelayOver;
  return (
    /*#__PURE__*/
    React.createElement(Group, {
      $disabled: disabled,
      className: className,
      style: style
    },
    /*#__PURE__*/
    React.createElement(Label, {
      htmlFor: id,
      $required: required,
      isError: shouldShowError,
      style: {
        marginBottom: "8px"
      }
    }, label),
    /*#__PURE__*/
    React.createElement(Aligner, null,
    /*#__PURE__*/
    React.createElement(TextInput, _extends({
      id: id,
      name: name,
      value: value,
      required: required,
      disabled: disabled,
      isError: shouldShowError,
      hasIcon: renderIcon,
      iconSize: iconSize,
      "aria-invalid": shouldShowError,
      as: as
    }, rest)), renderIcon &&
    /*#__PURE__*/
    React.createElement(IconPositioner, {
      size: iconSize
    }, renderIcon({
      size: iconSize
    }))),
    /*#__PURE__*/
    React.createElement(ErrorMessage, {
      "aria-live": "assertive"
    }, shouldShowError ? errorMessage : "\xA0"))
  );
};
TextInputGroup.propTypes = {
  as: PropTypes.oneOf(["input", "textarea"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  isErrorDelayOver: PropTypes.bool,
  isValid: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  renderIcon: PropTypes.func,
  required: PropTypes.bool,
  style: PropTypes.object,
  value: PropTypes.string
}; // Exported in case someone wants more control over the appearance

export var TextInput =
/*#__PURE__*/
styled.input.withConfig({
  displayName: "TextInput"
})(["width:100%;border:solid 2px;border-color:", ";border-radius:8px;padding:", ";color:", ";&::placeholder{color:", ";opacity:0.7;}background:none;&:focus,&:focus-within{border-color:#0a146e;}outline-offset:0;", ":-webkit-autofill{box-shadow:0 0 0px 1000px #fffde8 inset;}input&[type=\"number\"]::-webkit-inner-spin-button,input&[type=\"number\"]::-webkit-outer-spin-button{appearance:none;margin:0;}input&[type=\"number\"]{appearance:textfield;}"], function (_ref3) {
  var isError = _ref3.isError;
  return isError ? "#b30021" : "#757575";
}, function (_ref4) {
  var hasIcon = _ref4.hasIcon,
      iconSize = _ref4.iconSize;
  return hasIcon ? "12px calc(" + iconSize + " + 12px) 12px 12px" : "12px 12px";
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.paragraph.color;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.paragraph.color;
}, function (_ref7) {
  var disabled = _ref7.disabled;
  return disabled ? "border-style: dashed;" : "";
});
var Aligner =
/*#__PURE__*/
styled.div.withConfig({
  displayName: "Aligner"
})(["display:flex;align-items:center;position:relative;"]);
var IconPositioner =
/*#__PURE__*/
styled.div.withConfig({
  displayName: "IconPositioner"
})(["position:absolute;width:", ";height:", ";right:12px;"], function (_ref8) {
  var size = _ref8.size;
  return size;
}, function (_ref9) {
  var size = _ref9.size;
  return size;
});
export var ErrorMessage =
/*#__PURE__*/
styled.div.withConfig({
  displayName: "ErrorMessage"
})(["color:#b30021;font-size:1.4rem;"]);
var Group =
/*#__PURE__*/
styled.div.withConfig({
  displayName: "Group"
})(["opacity:", ";"], function (_ref10) {
  var $disabled = _ref10.$disabled;
  return $disabled ? 0.38 : 1;
});